// Pure, dependency-free helpers for ordering/filtering tournament lists — the
// "My Tournaments" grid (js/app.js renderTournamentsPage) and the export-tournament
// picker (js/services/tournament-io.js getExportableTournamentList) both use these
// so the two lists share one definition of "order" and one definition of
// "matches this format/rating-type".
//
// No DOM access, no imports of state.js / history-db.js / firebase — keep this
// trivially unit-testable (see tests/tournament-sort.test.js) and reusable from
// any future view (e.g. public/spectator tournament lists).

// Sort-order option values, shared by the "Order" select in the My Tournaments
// Sort & Filter panel and the persisted localStorage shape.
export const SORT_ORDERS = {
    CLOSURE: 'closure',
    NEWEST: 'newest',
    OLDEST: 'oldest'
};

// localStorage key both js/app.js (My Tournaments panel, writer) and
// js/services/tournament-io.js (export picker, reader of `order` only) use, so
// the two stay in sync without either importing the other (would be circular —
// app.js already imports tournament-io.js's initTournamentIO).
export const MY_TOURNAMENTS_SORT_STORAGE_KEY = 'myTournamentsSort';

const DEFAULT_ORDER = SORT_ORDERS.CLOSURE;

// Tournament ids are minted as `Date.now().toString()` (imported tournaments use
// `Date.now() + '_' + rand`) — the numeric prefix before any `_` is the creation
// time in epoch ms. Returns 0 if the id is missing or unparsable.
function getIdTimestamp(id) {
    if (id === undefined || id === null) return 0;
    const prefix = String(id).split('_')[0];
    const n = parseInt(prefix, 10);
    return Number.isFinite(n) ? n : 0;
}

// Unwraps a list entry to its tournament data object. Entries come in two
// shapes depending on caller: a raw history entry `{ id, data, ... }` (data
// holds the actual tournament fields) or an already-unwrapped tournament/data
// object (e.g. the live state.activeTournament spread with isActive: true).
function getData(entry) {
    if (!entry) return {};
    return entry.data || entry;
}

// Resolves the "closure/creation recency" sort key for a tournament entry:
// data.closedAt (stamped by closeTournament when a tournament is archived) if
// present and numeric, else the entry/data id's numeric creation-time prefix,
// else 0. Higher = more recently closed/created.
export function getClosureTimestamp(entry) {
    if (!entry) return 0;
    const data = getData(entry);
    if (typeof data.closedAt === 'number' && Number.isFinite(data.closedAt)) {
        return data.closedAt;
    }
    const id = entry.id !== undefined && entry.id !== null ? entry.id : data.id;
    return getIdTimestamp(id);
}

// Resolves a tournament's start-date timestamp (epoch ms) for "newest/oldest"
// ordering, or null if there is no usable date. Checks data.startDate first
// (canonical field), then the entry/data `date` field used by history/export
// list shapes.
function getStartDateTimestamp(entry) {
    if (!entry) return null;
    const data = getData(entry);
    const raw = data.startDate || entry.date || data.date;
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : null;
}

// Comparator for two entries under the given order (does not consider
// isActive pinning — see sortMyTournaments). Entries with no usable start date
// always sort after ones that have one, for NEWEST/OLDEST.
function compareByOrder(a, b, order) {
    if (order === SORT_ORDERS.NEWEST || order === SORT_ORDERS.OLDEST) {
        const ta = getStartDateTimestamp(a);
        const tb = getStartDateTimestamp(b);
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return order === SORT_ORDERS.OLDEST ? ta - tb : tb - ta;
    }
    // CLOSURE (default): most recently closed/created first.
    return getClosureTimestamp(b) - getClosureTimestamp(a);
}

// Sorts a list of tournament entries for display: the active tournament
// (entry.isActive === true) is always pinned first (there should be at most
// one), then the rest are ordered per `order` (one of SORT_ORDERS, defaults to
// CLOSURE). Returns a new array — does not mutate `list`.
export function sortMyTournaments(list, order) {
    const effectiveOrder = order || DEFAULT_ORDER;
    const arr = Array.isArray(list) ? list.slice() : [];
    arr.sort((a, b) => {
        const aActive = !!(a && a.isActive);
        const bActive = !!(b && b.isActive);
        if (aActive && !bActive) return -1;
        if (bActive && !aActive) return 1;
        return compareByOrder(a, b, effectiveOrder);
    });
    return arr;
}

// True if `entry` matches the given format filter. 'all' (or falsy) matches
// everything. A multi-group tournament matches if ANY of its groups matches,
// in addition to checking the top-level format field (legacy single-group
// tournaments only have the top-level field).
export function matchesFormat(entry, format) {
    if (!format || format === 'all') return true;
    const data = getData(entry);
    if (data.format === format) return true;
    if (Array.isArray(data.groups)) {
        return data.groups.some(g => g && g.format === format);
    }
    return false;
}

// True if `entry` matches the given rating-type filter. 'all' (or falsy)
// matches everything. Same any-group-matches rule as matchesFormat.
export function matchesRatingType(entry, type) {
    if (!type || type === 'all') return true;
    const data = getData(entry);
    if (data.type === type) return true;
    if (Array.isArray(data.groups)) {
        return data.groups.some(g => g && g.type === type);
    }
    return false;
}

// ---------------------------------------------------------------------------
// Public tournament lists (Ongoing / Upcoming / Finished spectator tabs).
// These share getData()/matchesFormat()/matchesRatingType() with the private
// list above but have their own per-tab default orderings and richer date/city
// filtering. Public entries are flat objects
// ({ name, format, type, startDate, endDate, location, status, groups? }) —
// getData() returns them unchanged (no `.data` wrapper).
// ---------------------------------------------------------------------------

// Order-option values for the public Sort & Filter panels. DEFAULT means "use
// this tab's natural ordering" (see sortPublicTournaments); NEWEST/OLDEST order
// by the tab-relevant date.
export const PUBLIC_SORT_ORDERS = {
    DEFAULT: 'default',
    NEWEST: 'newest',
    OLDEST: 'oldest'
};

// Parses a date-only string ('YYYY-MM-DD') to epoch ms, or null if missing /
// unparsable.
function toDateTs(str) {
    if (!str) return null;
    const t = new Date(str).getTime();
    return Number.isFinite(t) ? t : null;
}

// The [start, end] range of a tournament as timestamps, where end is clamped to
// be >= start (guards bad data where endDate precedes startDate). When endDate
// is absent the range is a single day — unless `openEndedWhenNoEnd` is set, in
// which case it extends indefinitely (the Ongoing tab uses this: the status
// computation in firebase-auth.js classifies a started tournament with no
// endDate as still ongoing, so the date filters must agree). Returns null if
// there is no usable start.
function getPublicRange(entry, openEndedWhenNoEnd = false) {
    const data = getData(entry);
    const startTs = toDateTs(data.startDate);
    if (startTs === null) return null;
    const endRaw = toDateTs(data.endDate);
    const endTs = endRaw === null
        ? (openEndedWhenNoEnd ? Infinity : startTs)
        : Math.max(startTs, endRaw);
    return { startTs, endTs };
}

// The timestamp a public tournament is ordered by for a given tab: finished
// tournaments order by end date (falling back to start), ongoing/upcoming by
// start date. Returns null when no usable date exists.
export function getPublicSortTimestamp(entry, category) {
    const data = getData(entry);
    if (category === 'finished') {
        return toDateTs(data.endDate) ?? toDateTs(data.startDate);
    }
    return toDateTs(data.startDate);
}

// Locale-aware, case-insensitive comparison of two entries' names — the
// Ongoing tab's default order, and the deterministic tiebreak everywhere else.
function comparePublicByName(a, b) {
    const na = String(getData(a).name || '');
    const nb = String(getData(b).name || '');
    return na.localeCompare(nb, undefined, { sensitivity: 'base' });
}

// Compares two entries by their tab-relevant date in the given direction
// ('asc' | 'desc'). Entries with no usable date sort last; equal dates fall
// back to a name comparison so ordering is stable.
function comparePublicByDate(a, b, category, direction) {
    const ta = getPublicSortTimestamp(a, category);
    const tb = getPublicSortTimestamp(b, category);
    if (ta === null && tb === null) return comparePublicByName(a, b);
    if (ta === null) return 1;
    if (tb === null) return -1;
    if (ta === tb) return comparePublicByName(a, b);
    return direction === 'asc' ? ta - tb : tb - ta;
}

// Orders a list of public tournament entries for the given tab. Returns a new
// array (does not mutate `list`).
//   order === NEWEST  -> tab-relevant date, most recent first
//   order === OLDEST  -> tab-relevant date, oldest first
//   order === DEFAULT (or falsy) -> per-tab natural order:
//     ongoing  : alphabetical by name (locale-aware, case-insensitive)
//     upcoming : soonest start date first
//     finished : most recently ended first (endDate, falling back to startDate)
export function sortPublicTournaments(list, category, order) {
    const arr = Array.isArray(list) ? list.slice() : [];
    const effectiveOrder = order || PUBLIC_SORT_ORDERS.DEFAULT;
    arr.sort((a, b) => {
        if (effectiveOrder === PUBLIC_SORT_ORDERS.NEWEST) {
            return comparePublicByDate(a, b, category, 'desc');
        }
        if (effectiveOrder === PUBLIC_SORT_ORDERS.OLDEST) {
            return comparePublicByDate(a, b, category, 'asc');
        }
        // DEFAULT
        if (category === 'upcoming') return comparePublicByDate(a, b, category, 'asc');
        if (category === 'finished') return comparePublicByDate(a, b, category, 'desc');
        // ongoing (and any unknown category): alphabetical
        return comparePublicByName(a, b);
    });
    return arr;
}

// True if `entry`'s date range covers the given specific date (i.e.
// startDate <= date <= (endDate || startDate)). An empty/unparsable date means
// "no date filter" (matches everything). An entry with no start date never
// matches a specific date. `openEndedWhenNoEnd` — see getPublicRange.
export function matchesSpecificDate(entry, dateStr, openEndedWhenNoEnd = false) {
    const target = toDateTs(dateStr);
    if (target === null) return true;
    const range = getPublicRange(entry, openEndedWhenNoEnd);
    if (!range) return false;
    return range.startTs <= target && target <= range.endTs;
}

// True if `entry`'s date range [start, end||start] overlaps the [from, to]
// period. Either bound may be empty (open-ended on that side); both empty means
// "no date filter" (matches everything). An entry with no start date never
// matches a bounded period. `openEndedWhenNoEnd` — see getPublicRange.
export function matchesPeriod(entry, fromStr, toStr, openEndedWhenNoEnd = false) {
    const fromTs = toDateTs(fromStr);
    const toTs = toDateTs(toStr);
    if (fromTs === null && toTs === null) return true;
    const range = getPublicRange(entry, openEndedWhenNoEnd);
    if (!range) return false;
    const lo = fromTs === null ? -Infinity : fromTs;
    const hi = toTs === null ? Infinity : toTs;
    // Ranges [startTs, endTs] and [lo, hi] overlap iff start <= hi and end >= lo.
    return range.startTs <= hi && range.endTs >= lo;
}

// Lazily-built set of lowercase country names (English + Norwegian bokmål/
// nynorsk via Intl.DisplayNames over all ISO 3166 alpha-2 codes, plus the raw
// codes themselves), used to skip country segments in extractCity. Arbiters
// commonly enter locations as "Venue, City, Country" — the country is noise
// for a city filter. Built once on first use (~676 codes × 3 locales); empty
// if Intl.DisplayNames is unavailable, which degrades to the old last-segment
// behavior.
let countryNames = null;
function getCountryNames() {
    if (countryNames) return countryNames;
    countryNames = new Set();
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
        try {
            const displays = ['en', 'nb', 'nn'].map(loc =>
                new Intl.DisplayNames([loc], { type: 'region', fallback: 'code' }));
            const A = 'A'.charCodeAt(0);
            for (let i = 0; i < 26; i++) {
                for (let j = 0; j < 26; j++) {
                    const code = String.fromCharCode(A + i) + String.fromCharCode(A + j);
                    for (const dn of displays) {
                        const name = dn.of(code);
                        // fallback:'code' returns the code itself for unassigned
                        // codes — only real country names get added.
                        if (name && name !== code) {
                            countryNames.add(name.toLowerCase());
                            countryNames.add(code.toLowerCase());
                        }
                    }
                }
            }
        } catch (e) {
            // Leave the set as whatever was built; extraction still works.
        }
    }
    return countryNames;
}

// Extracts a display city from a free-text location string: the last non-empty
// comma-separated segment that is not a country name (in English/Norwegian) or
// ISO country code. "Ullevål Stadion, Østerås, Norge" -> "Østerås",
// "Oslo, Norway" -> "Oslo", "Oslo" -> "Oslo", "Norge" -> ''. Returns '' for
// empty/missing/non-string input or when every segment is a country.
export function extractCity(location) {
    if (!location || typeof location !== 'string') return '';
    const countries = getCountryNames();
    const parts = location.split(',');
    for (let i = parts.length - 1; i >= 0; i--) {
        const seg = parts[i].trim();
        if (seg && !countries.has(seg.toLowerCase())) return seg;
    }
    return '';
}

// True if `entry`'s extracted city matches the given city filter (case-
// insensitive). 'all' (or falsy) matches everything, including entries with no
// location.
export function matchesCity(entry, city) {
    if (!city || city === 'all') return true;
    const data = getData(entry);
    const c = extractCity(data.location);
    if (!c) return false;
    return c.toLowerCase() === String(city).toLowerCase();
}
